import type { TFunction } from 'i18next';

import type { MetricChartSpec } from '@/components/charts/generic';
import type { FeedingResolution } from '@/domain/feedingDashboard';

import { formatDashboardDay } from './dashboardDay';
import { timelineBucketLabel } from './timelineBucketLabel';
import { isoWeekStart, todayInPeriod } from './todayMarker';

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_DAYS = 7;

// Date-only keys parse as UTC midnight, so plain millisecond arithmetic cannot cross a DST
// change the way local-time dates would.
function addDays(day: string, days: number): string {
    return new Date(Date.parse(day) + days * DAY_MS).toISOString().slice(0, 10);
}

function daysBetween(from: string, to: string): number {
    return Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS) + 1;
}

export interface PartialWeekInput {
    /** The selected period start (`overview.from`). */
    periodStart: string;
    /** The selected period end (`overview.to`). */
    periodEnd: string;
    /** The site's day in progress, whose week the today marker already notes. */
    siteToday: string | null;
    resolution: FeedingResolution;
    /** The bucket keys the chart draws. */
    buckets: string[];
}

export interface PartialWeekSpan {
    /** The bucket key: the Monday of the week. */
    key: string;
    /** The first and last day of the period inside the week. */
    from: string;
    to: string;
    days: number;
}

/**
 * The weekly buckets the period cuts short: its first week when it starts after a Monday,
 * its last when it ends before a Sunday, or the one week a short period sits inside.
 *
 * A bucket the chart does not draw is left out, and so is the week the today marker marks
 * in progress, whose caption already says its values are incomplete (U2 keeps that style
 * for today alone).
 */
export function partialWeekSpans(input: PartialWeekInput): PartialWeekSpan[] {
    const { periodStart, periodEnd, resolution, buckets } = input;
    if (resolution !== 'week' || periodStart > periodEnd) return [];

    const firstKey = isoWeekStart(periodStart);
    const lastKey = isoWeekStart(periodEnd);
    const spans: PartialWeekSpan[] = [];
    const add = (key: string, from: string, to: string): void => {
        const days = daysBetween(from, to);
        if (days < WEEK_DAYS) spans.push({ key, from, to, days });
    };
    if (firstKey === lastKey) {
        add(firstKey, periodStart, periodEnd);
    } else {
        add(firstKey, periodStart, addDays(firstKey, WEEK_DAYS - 1));
        add(lastKey, lastKey, periodEnd);
    }

    const today = todayInPeriod(input.siteToday, periodEnd);
    const markedKey = today === null ? null : isoWeekStart(today);
    return spans.filter((span) => span.key !== markedKey && buckets.includes(span.key));
}

export interface PartialWeeks {
    /** One sentence per partial week, for the caption under the chart; null when none. */
    caption: string | null;
    /**
     * The chart's `tooltipExtraLines`: a partial week's own sentence under its bar. Absent
     * when no week is partial, so every other chart keeps its tooltip as it was.
     */
    tooltipExtraLines: MetricChartSpec['tooltipExtraLines'];
}

/**
 * Notes on a weekly timeline's partial first and last week (see `partialWeekSpans`).
 *
 * Their feed totals cover fewer than seven days, so a two-day last week otherwise reads
 * as feeding collapsing that week.
 */
export function partialWeeks(input: PartialWeekInput, t: TFunction): PartialWeeks {
    const lines = new Map<string, string>();
    for (const span of partialWeekSpans(input)) {
        lines.set(
            span.key,
            t('feedingModule.charts.partialWeek', {
                week: timelineBucketLabel(span.key, 'week'),
                count: span.days,
                from: formatDashboardDay(span.from),
                to: formatDashboardDay(span.to),
            })
        );
    }
    if (lines.size === 0) return { caption: null, tooltipExtraLines: undefined };

    return {
        caption: [...lines.values()].join(' '),
        tooltipExtraLines: (index) => {
            const line = lines.get(input.buckets[index]);
            return line === undefined ? [] : [line];
        },
    };
}
